import React from 'react';
import {
  Monitor,
  Tablet,
  Smartphone,
  Undo2,
  Redo2,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertCircle,
  Save,
  X,
  Layers,
  Sparkles,
  ExternalLink,
} from 'lucide-react';

export default function BuilderToolbar({
  document = {},
  viewport = 'desktop', // desktop, tablet, mobile
  setViewport,
  isPreviewMode = false,
  setIsPreviewMode,
  isNavigatorOpen = false,
  setIsNavigatorOpen,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
  isApproved = false,
  canApprove = true,
  validationErrors = [],
  onApprovePreview,
  onResetApproval,
  onSave,
  onSaveDraft,
  isSaving = false,
  onExit,
  onDiscard,
  onUpdateDocument,
}) {
  const isLive = document.status === 'published' || document.status === 'active';

  return (
    <header className="min-h-20 bg-white border-b border-slate-200 px-4 py-2 flex items-center justify-between gap-4 z-40 select-none shadow-xs">
      {/* Left: Page Meta & Route */}
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={onExit}
          className="p-2 rounded-xl text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
          title="Exit Page Builder"
        >
          <X className="w-5 h-5" />
        </button>

        <button
          type="button"
          onClick={onDiscard || onExit}
          className="px-2.5 py-1 text-xs font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-lg border border-rose-200 transition"
          title="Discard all unsaved changes and exit"
        >
          Discard
        </button>

        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <input aria-label="Page title" value={document.title || ''} onChange={(e) => onUpdateDocument?.((prev) => ({ ...prev, title: e.target.value }))} placeholder="Page title" className="w-40 border border-slate-200 rounded px-2 py-1 text-xs font-bold text-slate-900" />
            <input aria-label="Manual page slug" value={document.slug || ''} onChange={(e) => onUpdateDocument?.((prev) => ({ ...prev, slug: e.target.value, route: !prev.route || prev.route === '/content/' + prev.slug ? '/content/' + e.target.value : prev.route }))} placeholder="manual-slug" pattern="[a-z0-9]+(?:-[a-z0-9]+)*" className="w-32 font-mono text-[10px] text-slate-700 bg-slate-50 px-2 py-1 rounded border border-slate-200 hidden sm:inline-block" />
            <input aria-label="Menu label override" value={document.navbar_label || ''} onChange={(e) => onUpdateDocument?.((prev) => ({ ...prev, navbar_label: e.target.value }))} placeholder="Menu label (optional)" className="w-36 border border-slate-200 rounded px-2 py-1 text-xs text-slate-700 hidden md:inline-block" />
            <input aria-label="Existing website route" value={document.route || ''} onChange={(e) => onUpdateDocument?.((prev) => ({ ...prev, route: e.target.value }))} placeholder="/about" className="w-28 font-mono text-[10px] text-slate-700 bg-slate-50 px-2 py-1 rounded border border-slate-200 hidden lg:inline-block" />
          </div>

          <div className="flex items-center gap-2 text-[11px] mt-0.5">
            {/* Status Picker */}
            <select
              value={document.status || 'draft'}
              onChange={(e) => onUpdateDocument && onUpdateDocument((prev) => ({ ...prev, status: e.target.value }))}
              className={`font-semibold rounded px-1.5 py-0.5 border text-[10px] uppercase cursor-pointer ${
                isLive
                  ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                  : 'bg-amber-50 text-amber-800 border-amber-300'
              }`}
            >
              <option value="draft">🟡 Draft (Offline)</option>
              <option value="published">🟢 Published (Live)</option>
              <option value="inactive">🔴 Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Middle: Viewport Switcher & History (Desktop / Tablet / Mobile) */}
      <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl border border-slate-200">
        <button
          type="button"
          onClick={() => setViewport('desktop')}
          className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all ${
            viewport === 'desktop'
              ? 'bg-white text-[#2b3380] shadow-xs font-bold'
              : 'text-slate-500 hover:text-slate-900'
          }`}
          title="Desktop View (Full Width)"
        >
          <Monitor className="w-4 h-4" />
          <span className="hidden md:inline">Desktop</span>
        </button>

        <button
          type="button"
          onClick={() => setViewport('tablet')}
          className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all ${
            viewport === 'tablet'
              ? 'bg-white text-[#2b3380] shadow-xs font-bold'
              : 'text-slate-500 hover:text-slate-900'
          }`}
          title="Tablet View (768px)"
        >
          <Tablet className="w-4 h-4" />
          <span className="hidden md:inline">Tablet</span>
        </button>

        <button
          type="button"
          onClick={() => setViewport('mobile')}
          className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all ${
            viewport === 'mobile'
              ? 'bg-white text-[#2b3380] shadow-xs font-bold'
              : 'text-slate-500 hover:text-slate-900'
          }`}
          title="Mobile View (375px)"
        >
          <Smartphone className="w-4 h-4" />
          <span className="hidden md:inline">Mobile</span>
        </button>

        <div className="h-4 w-px bg-slate-300 mx-1" />

        {/* Undo / Redo */}
        <button
          type="button"
          disabled={!canUndo}
          onClick={onUndo}
          className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 disabled:opacity-30 transition-colors"
          title="Undo (Ctrl+Z)"
        >
          <Undo2 className="w-4 h-4" />
        </button>
        <button
          type="button"
          disabled={!canRedo}
          onClick={onRedo}
          className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 disabled:opacity-30 transition-colors"
          title="Redo (Ctrl+Y)"
        >
          <Redo2 className="w-4 h-4" />
        </button>
      </div>

      {/* Right: Preview Mode, Layers, Approval & Save */}
      <div className="flex items-center gap-2">
        {/* Toggle Layers / Navigator */}
        <button
          type="button"
          onClick={() => setIsNavigatorOpen(!isNavigatorOpen)}
          className={`px-3 py-1.5 rounded-xl border text-xs font-semibold flex items-center gap-1.5 transition-all ${
            isNavigatorOpen
              ? 'bg-indigo-50 border-indigo-200 text-[#2b3380]'
              : 'bg-white border-slate-200 text-slate-600 hover:text-slate-900'
          }`}
          title="Toggle Layers / Navigator Tree"
        >
          <Layers className="w-4 h-4" />
          <span className="hidden lg:inline">Navigator</span>
        </button>

        {/* Clean Preview Toggle */}
        <button
          type="button"
          onClick={() => setIsPreviewMode(!isPreviewMode)}
          className={`px-3 py-1.5 rounded-xl border text-xs font-semibold flex items-center gap-1.5 transition-all ${
            isPreviewMode
              ? 'bg-slate-900 border-slate-900 text-white shadow-xs'
              : 'bg-white border-slate-200 text-slate-600 hover:text-slate-900'
          }`}
          title={isPreviewMode ? 'Exit Preview Mode' : 'Clean Preview (Hide Builder UI)'}
        >
          {isPreviewMode ? <EyeOff className="w-4 h-4 text-emerald-400" /> : <Eye className="w-4 h-4" />}
          <span className="hidden sm:inline">{isPreviewMode ? 'Editing Mode' : 'Preview'}</span>
        </button>

        {/* Preview Approval Button */}
        {!isApproved ? (
          <button
            type="button"
            disabled={!canApprove}
            onClick={onApprovePreview}
            className={`px-4 py-2 rounded-xl text-xs font-bold shadow-xs transition-all flex items-center gap-1.5 ${
              canApprove
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer active:scale-[0.98]'
                : 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
            }`}
            title={canApprove ? 'Review and approve current preview to enable Save' : validationErrors[0] || 'Fill required fields'}
          >
            <CheckCircle2 className="w-4 h-4" />
            <span>Approve Preview</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={onResetApproval}
            className="px-3 py-2 rounded-xl text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-300 hover:bg-emerald-100 transition-colors flex items-center gap-1"
            title="Preview is approved. Click to edit and reset."
          >
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span className="hidden sm:inline">Approved</span>
            <span className="text-[10px] text-emerald-600 underline ml-0.5">(Reset)</span>
          </button>
        )}

        <button type="button" disabled={isSaving} onClick={onSaveDraft} className="px-3 py-2 rounded-xl border border-slate-300 text-slate-700 text-xs font-semibold disabled:opacity-50">Save draft</button>
        {/* CRITICAL REQUIREMENT: Save Changes is strictly VISIBLE only when isApproved is true */}
        {isApproved && (
          <button
            type="button"
            disabled={isSaving}
            onClick={onSave}
            className="px-5 py-2 text-white font-bold rounded-xl text-xs shadow-md hover:shadow-lg transition-all flex items-center gap-1.5 cursor-pointer active:scale-[0.98] disabled:opacity-50 animate-in fade-in"
            style={{ backgroundColor: '#2b3380' }}
          >
            {isSaving ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        )}
      </div>
    </header>
  );
}
